import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";

const inputCls =
  "bg-white border border-[#0D1B2A]/20 focus:border-[#C5A059] focus:ring-2 focus:ring-[#C5A059]/30 rounded-xl px-3.5 py-2 text-[#0D1B2A] placeholder:text-gray-400 outline-none w-full transition-all text-sm";

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

const Field = ({ label, value, onChange, textarea, testId, placeholder }) => (
  <div>
    <label className="text-[10px] uppercase tracking-wider font-semibold text-[#0D1B2A]/50 block mb-1">{label}</label>
    {textarea ? (
      <textarea rows={2} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testId} placeholder={placeholder} />
    ) : (
      <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testId} placeholder={placeholder} />
    )}
  </div>
);

const ItemEditor = ({ item, fields, onChange, onDelete, testPrefix }) => (
  <div className="bg-[#FAF8F5] border border-[#0D1B2A]/10 rounded-2xl p-5 space-y-3" data-testid={`${testPrefix}-${item.id}`}>
    <div className="flex justify-end">
      <button onClick={onDelete} className="text-red-400 hover:text-red-600 p-1 transition-colors" data-testid={`${testPrefix}-delete-${item.id}`} aria-label="Delete item">
        <Trash2 size={15} />
      </button>
    </div>
    <div className="grid sm:grid-cols-2 gap-3">
      {fields.map((f) => (
        <div key={f.key} className={f.full ? "sm:col-span-2" : ""}>
          <Field
            label={f.label}
            textarea={f.textarea}
            value={item[f.key]}
            placeholder={f.placeholder}
            testId={`${testPrefix}-${f.key}-${item.id}`}
            onChange={(v) => onChange({ ...item, [f.key]: v })}
          />
        </div>
      ))}
    </div>
  </div>
);

const SECTIONS = {
  guide: [
    { key: "attractions", label: "Attractions (Things to Do)", blank: { name: "", category: "", description: "", image: "", distance: "" }, fields: [
      { key: "name", label: "Name" }, { key: "category", label: "Category" },
      { key: "description", label: "Description", textarea: true, full: true },
      { key: "image", label: "Image URL", full: true }, { key: "distance", label: "Distance (e.g. 5 min)" },
    ]},
    { key: "restaurants", label: "Restaurants (Places to Eat)", blank: { name: "", cuisine: "", description: "", phone: "", price: "" }, fields: [
      { key: "name", label: "Name" }, { key: "cuisine", label: "Cuisine" },
      { key: "description", label: "Description", textarea: true, full: true },
      { key: "phone", label: "Phone" }, { key: "price", label: "Price ($/$$/$$$)" },
    ]},
    { key: "guide", label: "Visitor Guide Chapters", blank: { title: "", description: "", tips: "" }, fields: [
      { key: "title", label: "Title", full: true },
      { key: "description", label: "Description", textarea: true, full: true },
      { key: "tips", label: "Insider Tip", full: true },
    ]},
  ],
  hotel: [
    { key: "rooms", label: "Rooms & Suites", blank: { name: "", rate: 0, capacity: "", image: "", amenities: [], description: "" }, fields: [
      { key: "name", label: "Room name" }, { key: "rate", label: "Nightly rate ($)" },
      { key: "capacity", label: "Capacity (e.g. 2 Guests)" }, { key: "image", label: "Image URL" },
      { key: "amenities", label: "Amenities (comma separated)", full: true, placeholder: "King Bed, Lake View, Balcony" },
      { key: "description", label: "Description", textarea: true, full: true },
    ]},
  ],
};

export const ContentTab = ({ section }) => {
  const [content, setContent] = useState(null);
  const [activeList, setActiveList] = useState(SECTIONS[section][0].key);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content").then((res) => setContent(res.data)).catch(() => toast.error("Could not load content"));
  }, []);

  if (!content) return <div className="w-8 h-8 border-2 border-[#C5A059] border-t-transparent rounded-full animate-spin mx-auto my-16" data-testid="content-loading" />;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", {
        hotel: content.hotel,
        rooms: content.rooms,
        attractions: content.attractions,
        restaurants: content.restaurants,
        guide: content.guide,
      });
      toast.success("Content saved — live on the site now.");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const updateList = (key, items) => setContent((c) => ({ ...c, [key]: items }));

  return (
    <div data-testid={`content-tab-${section}`}>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h2 className="font-serif-display text-2xl font-semibold text-[#0A192F]">
          {section === "guide" ? "Town Guide & Dining" : "Hotel Info & Rooms"}
        </h2>
        <button onClick={save} disabled={saving} className="flex items-center gap-2 bg-[#C5A059] text-[#0A192F] hover:bg-[#D4AF37] rounded-full px-6 py-2.5 text-sm font-semibold transition-all disabled:opacity-60" data-testid="content-save-button">
          <Save size={15} /> {saving ? "Saving…" : "Save & Publish"}
        </button>
      </div>

      {section === "hotel" && (
        <div className="bg-[#FAF8F5] border border-[#0D1B2A]/10 rounded-2xl p-5 mb-8" data-testid="hotel-info-editor">
          <h3 className="font-serif-display text-lg font-semibold text-[#0A192F] mb-4">Hotel Information</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Hotel name" value={content.hotel.name} onChange={(v) => setContent((c) => ({ ...c, hotel: { ...c.hotel, name: v } }))} testId="hotel-name-input" />
            <Field label="Phone (display)" value={content.hotel.phone} onChange={(v) => setContent((c) => ({ ...c, hotel: { ...c.hotel, phone: v } }))} testId="hotel-phone-input" />
            <Field label="Phone (tel: link)" value={content.hotel.phone_href} onChange={(v) => setContent((c) => ({ ...c, hotel: { ...c.hotel, phone_href: v } }))} testId="hotel-phone-href-input" placeholder="tel:+13346870000" />
            <Field label="Email" value={content.hotel.email} onChange={(v) => setContent((c) => ({ ...c, hotel: { ...c.hotel, email: v } }))} testId="hotel-email-input" />
            <Field label="Enquiry alerts sent to (notification email)" value={content.hotel.notify_email} onChange={(v) => setContent((c) => ({ ...c, hotel: { ...c.hotel, notify_email: v } }))} testId="hotel-notify-email-input" placeholder="you@yourhotel.com" />
            <Field label="Address" value={content.hotel.address} onChange={(v) => setContent((c) => ({ ...c, hotel: { ...c.hotel, address: v } }))} testId="hotel-address-input" />
            <Field label="Tagline" value={content.hotel.tagline} onChange={(v) => setContent((c) => ({ ...c, hotel: { ...c.hotel, tagline: v } }))} testId="hotel-tagline-input" />
            <div className="sm:col-span-2">
              <Field label="Hero intro paragraph" textarea value={content.hotel.intro} onChange={(v) => setContent((c) => ({ ...c, hotel: { ...c.hotel, intro: v } }))} testId="hotel-intro-input" />
            </div>
            <div className="sm:col-span-2">
              <Field label="Hero image URL" value={content.hotel.hero_image} onChange={(v) => setContent((c) => ({ ...c, hotel: { ...c.hotel, hero_image: v } }))} testId="hotel-hero-image-input" />
            </div>
            <div className="sm:col-span-2">
              <Field
                label="Hotel features (one per line: Title | Short description)"
                textarea
                value={(content.hotel.features || []).map((f) => `${f.title} | ${f.description}`).join("\n")}
                onChange={(v) =>
                  setContent((c) => ({
                    ...c,
                    hotel: {
                      ...c.hotel,
                      features: v.split("\n").map((line) => {
                        const [title, ...rest] = line.split("|");
                        return { key: (title || "").trim().toLowerCase().replace(/[^a-z]+/g, "-"), title: (title || "").trim(), description: rest.join("|").trim() };
                      }).filter((f) => f.title),
                    },
                  }))
                }
                testId="hotel-features-input"
              />
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-5">
        {SECTIONS[section].map((s) => (
          <button
            key={s.key}
            onClick={() => setActiveList(s.key)}
            className={`text-xs uppercase tracking-wider font-semibold rounded-full px-4 py-2 transition-all ${activeList === s.key ? "bg-[#0A192F] text-[#F8F6F0]" : "bg-white/60 text-[#0A192F]/60 hover:bg-white"}`}
            data-testid={`content-section-${s.key}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {SECTIONS[section].filter((s) => s.key === activeList).map((s) => {
        const items = content[s.key] || [];
        return (
          <div key={s.key} className="space-y-4" data-testid={`content-list-${s.key}`}>
            <button
              onClick={() => updateList(s.key, [...items, { ...s.blank, id: uid() }])}
              className="flex items-center gap-2 text-sm font-semibold text-[#0A192F] border border-dashed border-[#0D1B2A]/30 hover:border-[#C5A059] rounded-full px-5 py-2.5 transition-all"
              data-testid={`content-add-${s.key}`}
            >
              <Plus size={15} /> Add entry
            </button>
            {items.map((item, idx) => (
              <ItemEditor
                key={item.id || idx}
                item={item}
                fields={s.fields}
                testPrefix={`content-${s.key}`}
                onChange={(updated) => {
                  let next = { ...updated };
                  if (s.key === "rooms") {
                    next.rate = Number(next.rate) || 0;
                    if (typeof next.amenities === "string") next.amenities = next.amenities.split(",").map((a) => a.trim()).filter(Boolean);
                  }
                  updateList(s.key, items.map((it, i) => (i === idx ? next : it)));
                }}
                onDelete={() => updateList(s.key, items.filter((_, i) => i !== idx))}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
};
